// Historia Clínica Electrónica. Centro Medico → Clínica → Historia Clínica.
// Muestra alertas, evoluciones (SOAP/formularios + signos + dx), lista de
// problemas y vacunación. Nueva evolución con plantilla y firma.
// La vista por paciente (HistoriaClinicaPaciente) es COMPARTIDA con la ficha
// 360° (allí en modo soloLectura: sin crear/editar, solo consulta + Imprimir).
// Imprimir = documento compilado con logos y firmas (ModalHistoriaClinica).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileSignature, Loader2, Lock, Plus, Printer, Stethoscope, Syringe, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  AutoTextarea,
  Campo, Cie10, Diagnostico, EvolucionItem, Historia, Modal, PacienteRow, Plantilla,
  Problema, SignoVital, Switch, Vacuna, calcEdad, clasificacionIMCEdad, mesesEntre, useRefIMC, fieldCls, ESTADO_PROBLEMA, TIPO_DIAGNOSTICO, med, medRpc, esSchema,
  type SoapContenido, type Convenio,
} from './shared';
import { FormularioClinico, calcIMC, camposRequeridosFaltantes } from './FormularioClinico';
import { fechaLocal } from '@/lib/utils/fecha';
import { ModalHistoriaClinica } from './HistoriaClinicaDoc';
import { LOCALE } from '@/lib/utils/regional';

export function HistoriaClinica() {
  const [pacienteId, setPacienteId] = useState('');
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({
    queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Historia Clínica</h1>
        <select className={`${fieldCls} w-72`} value={pacienteId} onChange={(e) => setPacienteId(e.target.value)}>
          <option value="">— Selecciona un paciente —</option>
          {pacientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
        </select>
      </div>

      {!pacienteId && <Card><CardContent className="py-12 text-center text-muted-foreground">Selecciona un paciente para ver su historia clínica.</CardContent></Card>}
      {pacienteId && <HistoriaClinicaPaciente pacienteId={pacienteId} />}
    </div>
  );
}

// Vista HCE de un paciente — compartida por el módulo y la ficha 360°.
// soloLectura: sin «Nueva evolución» ni firmar/editar problemas y vacunas
// (en la ficha las consultas se crean desde Enfermedad Actual).
export function HistoriaClinicaPaciente({ pacienteId, soloLectura = false }: { pacienteId: string; soloLectura?: boolean }) {
  const qc = useQueryClient();
  const { data: historia, isFetching } = useQuery<Historia>({
    queryKey: ['med-historia', pacienteId],
    queryFn: () => medRpc<Historia>('med_historia', { _paciente_id: pacienteId }),
  });
  const [nueva, setNueva] = useState(false);
  const [hcModal, setHcModal] = useState(false);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['med-historia', pacienteId] });

  if (isFetching && !historia) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  if (!historia?.paciente) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!soloLectura && <Button onClick={() => setNueva(true)}><Plus className="w-4 h-4 mr-1" /> Nueva evolución</Button>}
        <Button variant="outline" onClick={() => setHcModal(true)}><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
      </div>

      <Alertas h={historia} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5"><Stethoscope className="w-4 h-4" /> Evoluciones ({historia.evoluciones.length})</h2>
          {historia.evoluciones.length === 0 && <Card><CardContent className="py-6 text-center text-muted-foreground text-sm">Sin evoluciones registradas</CardContent></Card>}
          {historia.evoluciones.map((ev) => <EvolucionCard key={ev.evolucion.id} item={ev} soloLectura={soloLectura} onChanged={refrescar} />)}
        </div>
        <div className="space-y-4">
          <Problemas pacienteId={pacienteId} problemas={historia.problemas} soloLectura={soloLectura} onChanged={refrescar} />
          <Vacunas pacienteId={pacienteId} vacunas={historia.vacunas} soloLectura={soloLectura} onChanged={refrescar} />
        </div>
      </div>

      {nueva && (
        <NuevaEvolucion pacienteId={pacienteId} fechaNacimiento={historia.paciente.fecha_nacimiento} genero={historia.paciente.genero}
          onClose={() => setNueva(false)} onSaved={() => { setNueva(false); refrescar(); }} />
      )}
      {hcModal && <ModalHistoriaClinica pacienteId={pacienteId} nombre={historia.paciente.razon_social} onClose={() => setHcModal(false)} />}
    </div>
  );
}

function Alertas({ h }: { h: Historia }) {
  const a = h.alertas; const p = h.paciente!;
  const hayAlerta = a.alergias || (a.problemas_criticos?.length ?? 0) > 0;
  return (
    <Card>
      <CardContent className="py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span className="font-semibold">{p.razon_social}</span>
        <span className="text-muted-foreground">{p.identificacion ?? 's/i'} · {calcEdad(p.fecha_nacimiento)} · {p.genero ?? '—'} · {p.tipo_sangre ?? 'Sangre N/D'}</span>
        {hayAlerta && (
          <span className="inline-flex items-center gap-1.5 rounded bg-urgente-soft text-urgente px-2 py-0.5">
            <AlertTriangle className="w-4 h-4" />
            {a.alergias ? `Alergias: ${a.alergias}` : ''}
            {a.problemas_criticos?.length ? ` ${a.alergias ? '·' : ''} ${a.problemas_criticos.join(', ')}` : ''}
          </span>
        )}
        {a.medicamentos && <span className="text-muted-foreground">Medicación: {a.medicamentos}</span>}
      </CardContent>
    </Card>
  );
}

function EvolucionCard({ item, soloLectura = false, onChanged }: { item: EvolucionItem; soloLectura?: boolean; onChanged: () => void }) {
  const e = item.evolucion;
  const s = item.signos?.[0];
  const firmar = useMutation({
    mutationFn: () => medRpc('med_firmar_evolucion', { _id: e.id }),
    onSuccess: () => { toast.success('Evolución firmada'); onChanged(); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  return (
    <Card>
      <CardContent className="py-3 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-medium">{new Date(e.fecha).toLocaleString(LOCALE)} · {item.medico}{item.especialidad ? ` · ${item.especialidad}` : ''}</span>
          {e.firmada
            ? <span className="inline-flex items-center gap-1 text-xs text-estable"><Lock className="w-3.5 h-3.5" /> Firmada</span>
            : soloLectura
              ? <span className="text-xs text-muted-foreground">Sin firmar</span>
              : <Button size="sm" variant="outline" isLoading={firmar.isPending} onClick={() => firmar.mutate()}><FileSignature className="w-3.5 h-3.5 mr-1" /> Firmar</Button>}
        </div>
        {e.motivo && <p><b>Motivo:</b> {e.motivo}</p>}
        {e.subjetivo && <p><b>S:</b> {e.subjetivo}</p>}
        {e.objetivo && <p><b>O:</b> {e.objetivo}</p>}
        {e.analisis && <p><b>A:</b> {e.analisis}</p>}
        {e.plan && <p><b>P:</b> {e.plan}</p>}
        {e.datos && Object.keys(e.datos).length > 0 && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
            {Object.entries(e.datos).filter(([, v]) => v !== '' && v !== null && v !== false).map(([k, v]) => (
              <div key={k} className="flex gap-1">
                <dt className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}:</dt>
                <dd>{v === true ? 'Sí' : String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
        {item.diagnosticos.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.diagnosticos.map((d, i) => (
              <span key={i} className={`rounded px-2 py-0.5 text-xs ${d.es_principal ? 'bg-primary/15 text-primary' : 'bg-muted'}`}>
                {d.cie10_codigo ? `${d.cie10_codigo} · ` : ''}{d.descripcion}{d.tipo === 'presuntivo' ? ' (?)' : ''}
              </span>
            ))}
          </div>
        )}
        {s && (
          <p className="font-mono tabular-nums text-xs text-muted-foreground">
            {s.presion_sistolica && `TA ${s.presion_sistolica}/${s.presion_diastolica} · `}
            {s.frecuencia_cardiaca && `FC ${s.frecuencia_cardiaca} · `}
            {s.temperatura && `T° ${s.temperatura} · `}
            {s.spo2 && `SpO₂ ${s.spo2}% · `}
            {s.peso && `Peso ${s.peso}kg`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Problemas activos ----
function Problemas({ pacienteId, problemas, soloLectura = false, onChanged }: { pacienteId: string; problemas: Problema[]; soloLectura?: boolean; onChanged: () => void }) {
  const [desc, setDesc] = useState('');
  const [critico, setCritico] = useState(false);
  const agregar = useMutation({
    mutationFn: () => med.create('problema', { paciente_id: pacienteId, descripcion: desc, es_critico: critico }),
    onSuccess: () => { setDesc(''); setCritico(false); toast.success('Problema agregado'); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cambiarEstado = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: string }) => med.update('problema', id, { estado, fecha_resolucion: estado === 'resuelto' ? fechaLocal() : null }),
    onSuccess: onChanged, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => med.update('problema', id, { deleted_at: new Date().toISOString() }),
    onSuccess: onChanged, onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Lista de problemas</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {problemas.length === 0 && <p className="text-xs text-muted-foreground">Sin problemas</p>}
        {problemas.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 text-sm">
            <span className={`flex-1 ${p.estado === 'resuelto' ? 'line-through text-muted-foreground' : ''}`}>
              {p.es_critico && <AlertTriangle className="inline w-3.5 h-3.5 text-red-500 mr-1" />}{p.descripcion}
            </span>
            {soloLectura ? <span className="text-xs capitalize text-muted-foreground">{p.estado}</span> : (<>
              <select className="text-xs rounded border bg-background px-1 py-0.5 capitalize" value={p.estado}
                onChange={(e) => cambiarEstado.mutate({ id: p.id, estado: e.target.value })}>
                {ESTADO_PROBLEMA.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button className="text-red-500" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(p.id)}><Trash2 className="w-3.5 h-3.5" /></button>
            </>)}
          </div>
        ))}
        {!soloLectura && (<>
          <div className="flex items-center gap-2 pt-1">
            <input className={`${fieldCls} h-9`} placeholder="Nuevo problema…" value={desc} onChange={(e) => setDesc(e.target.value)} />
            <Button size="sm" variant="outline" isLoading={agregar.isPending} disabled={!desc.trim()} onClick={() => agregar.mutate()}><Plus className="w-4 h-4" /></Button>
          </div>
          <Switch checked={critico} onChange={setCritico} label="Crítico" />
        </>)}
      </CardContent>
    </Card>
  );
}

// ---- Vacunas ----
const vacVacio = { vacuna: '', dosis: '', fecha: fechaLocal(), lote: '' };
function Vacunas({ pacienteId, vacunas, soloLectura = false, onChanged }: { pacienteId: string; vacunas: Vacuna[]; soloLectura?: boolean; onChanged: () => void }) {
  const [f, setF] = useState(vacVacio);
  const [abierto, setAbierto] = useState(false);
  const agregar = useMutation({
    mutationFn: () => med.create('vacuna', { paciente_id: pacienteId, vacuna: f.vacuna, dosis: f.dosis || null, fecha: f.fecha, lote: f.lote || null }),
    onSuccess: () => { setF(vacVacio); setAbierto(false); toast.success('Vacuna registrada'); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => med.update('vacuna', id, { deleted_at: new Date().toISOString() }),
    onSuccess: onChanged, onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Card>
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-1.5"><Syringe className="w-4 h-4" /> Vacunación</CardTitle>
        {!soloLectura && <button className="text-primary" title="Agregar" aria-label="Agregar" onClick={() => setAbierto(true)}><Plus className="w-4 h-4" /></button>}
      </CardHeader>
      <CardContent className="space-y-1.5">
        {vacunas.length === 0 && <p className="text-xs text-muted-foreground">Sin registros</p>}
        {vacunas.map((v) => (
          <div key={v.id} className="flex items-center justify-between text-sm">
            <span>{v.vacuna} {v.dosis && <span className="text-muted-foreground">· {v.dosis}</span>} <span className="text-muted-foreground text-xs">{v.fecha}</span></span>
            {!soloLectura && <button className="text-red-500" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(v.id)}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </CardContent>
      {abierto && (
        <Modal title="Registrar vacuna" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Vacuna *"><input className={fieldCls} value={f.vacuna} onChange={(e) => setF({ ...f, vacuna: e.target.value })} /></Campo>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Dosis"><input className={fieldCls} value={f.dosis} onChange={(e) => setF({ ...f, dosis: e.target.value })} /></Campo>
              <Campo label="Fecha"><input type="date" className={fieldCls} value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} /></Campo>
              <Campo label="Lote"><input className={fieldCls} value={f.lote} onChange={(e) => setF({ ...f, lote: e.target.value })} /></Campo>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={agregar.isPending} disabled={!f.vacuna.trim()} onClick={() => agregar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ---- Nueva evolución ----
const sigVacio: SignoVital = {
  presion_sistolica: null, presion_diastolica: null, frecuencia_cardiaca: null, frecuencia_resp: null,
  temperatura: null, spo2: null, peso: null, talla: null, glucosa: null, dolor: null, observaciones: null,
};

export function NuevaEvolucion({ pacienteId, onClose, onSaved, fechaNacimiento, genero }: {
  pacienteId: string; onClose: () => void; onSaved: () => void;
  fechaNacimiento?: string | null; genero?: string | null;
}) {
  const { data: refIMC } = useRefIMC(genero);
  const { data: plantillas = [] } = useQuery<Plantilla[]>({ queryKey: ['med-plantillas-sel'], queryFn: () => med.list<Plantilla>('plantilla', '?select=*&deleted_at=is.null&es_activo=is.true&categoria=not.in.(informe,consentimiento_informado,orden_ingreso,protocolo_operatorio,procedimiento_tipo,receta,pedido,cobro)&order=nombre') });
  const { data: convenios = [] } = useQuery<Convenio[]>({ queryKey: ['med-convenios', 'todos'], queryFn: () => medRpc<Convenio[]>('med_convenios', { _tipo: null }) });

  const [plantillaId, setPlantillaId] = useState('');
  const [convenioId, setConvenioId] = useState('');
  const [soap, setSoap] = useState({ motivo: '', subjetivo: '', objetivo: '', analisis: '', plan: '', observaciones: '' });
  const [datos, setDatos] = useState<Record<string, unknown>>({});
  const [signos, setSignos] = useState<SignoVital>(sigVacio);
  const [dx, setDx] = useState<Diagnostico[]>([]);

  const plantillaSel = plantillas.find((p) => p.id === plantillaId);
  const formato = plantillaSel && esSchema(plantillaSel.contenido) ? plantillaSel.contenido : null;

  const aplicarPlantilla = (id: string) => {
    setPlantillaId(id);
    const c = plantillas.find((p) => p.id === id)?.contenido;
    if (!c) return;
    if (esSchema(c)) { setDatos({}); return; }  // formato dinámico: arranca vacío
    const sc = c as SoapContenido;
    setSoap((s) => ({ ...s, motivo: sc.motivo ?? s.motivo, subjetivo: sc.subjetivo ?? s.subjetivo, objetivo: sc.objetivo ?? s.objetivo, analisis: sc.analisis ?? s.analisis, plan: sc.plan ?? s.plan }));
  };

  const sigNum = (k: keyof SignoVital, v: string) => setSignos((s) => ({ ...s, [k]: v === '' ? null : Number(v) }));

  const guardar = useMutation({
    mutationFn: async (firmar: boolean) => {
      // Al firmar un formato dinámico, exigir los campos marcados como requeridos.
      if (firmar && formato) {
        const faltan = camposRequeridosFaltantes(formato, datos);
        if (faltan.length) throw new Error(`Faltan campos requeridos: ${faltan.join(', ')}`);
      }
      const r = await medRpc<{ id: string }>('med_guardar_evolucion', {
        _data: {
          paciente_id: pacienteId, plantilla_id: plantillaId || null,
          convenio_id: convenioId || null,
          ...soap, motivo: soap.motivo || (datos.motivo as string) || '',
          datos: formato ? datos : {}, signos: formato ? sigVacio : signos, diagnosticos: dx,
        },
      });
      if (firmar) await medRpc('med_firmar_evolucion', { _id: r.id });
      return r;
    },
    onSuccess: () => { toast.success('Evolución guardada'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Nueva evolución" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Formato / plantilla">
            <select className={fieldCls} value={plantillaId} onChange={(e) => aplicarPlantilla(e.target.value)}>
              <option value="">— Ninguna —</option>{plantillas.map((p) => <option key={p.id} value={p.id}>{p.codigo ? `${p.codigo} · ` : ''}{p.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Convenio (empresa/aseguradora)">
            <select className={fieldCls} value={convenioId} onChange={(e) => setConvenioId(e.target.value)}>
              <option value="">— Ninguno —</option>{convenios.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.tipo})</option>)}
            </select>
          </Campo>
        </div>

        {formato ? (
          <FormularioClinico schema={formato} valores={datos} onChange={setDatos} />
        ) : (<>
        <Campo label="Motivo de consulta"><AutoTextarea value={soap.motivo} onChange={(e) => setSoap({ ...soap, motivo: e.target.value })} /></Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Subjetivo (S)"><AutoTextarea value={soap.subjetivo} onChange={(e) => setSoap({ ...soap, subjetivo: e.target.value })} /></Campo>
          <Campo label="Objetivo (O)"><AutoTextarea value={soap.objetivo} onChange={(e) => setSoap({ ...soap, objetivo: e.target.value })} /></Campo>
          <Campo label="Análisis (A)"><AutoTextarea value={soap.analisis} onChange={(e) => setSoap({ ...soap, analisis: e.target.value })} /></Campo>
          <Campo label="Plan (P)"><AutoTextarea value={soap.plan} onChange={(e) => setSoap({ ...soap, plan: e.target.value })} /></Campo>
        </div>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground">Signos vitales</h4>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            <Campo label="TA sist."><input type="number" className={fieldCls} onChange={(e) => sigNum('presion_sistolica', e.target.value)} /></Campo>
            <Campo label="TA diast."><input type="number" className={fieldCls} onChange={(e) => sigNum('presion_diastolica', e.target.value)} /></Campo>
            <Campo label="FC"><input type="number" className={fieldCls} onChange={(e) => sigNum('frecuencia_cardiaca', e.target.value)} /></Campo>
            <Campo label="FR"><input type="number" className={fieldCls} onChange={(e) => sigNum('frecuencia_resp', e.target.value)} /></Campo>
            <Campo label="T° (°C)"><input type="number" step="0.1" className={fieldCls} onChange={(e) => sigNum('temperatura', e.target.value)} /></Campo>
            <Campo label="SpO₂ (%)"><input type="number" className={fieldCls} onChange={(e) => sigNum('spo2', e.target.value)} /></Campo>
            <Campo label="Peso (kg)"><input type="number" step="0.1" className={fieldCls} onChange={(e) => sigNum('peso', e.target.value)} /></Campo>
            <Campo label="Talla (cm)"><input type="number" step="0.1" className={fieldCls} onChange={(e) => sigNum('talla', e.target.value)} /></Campo>
            <Campo label="IMC (auto)">
              <input readOnly tabIndex={-1} className={`${fieldCls} bg-muted/50`} value={calcIMC(signos.peso, signos.talla) ?? ''} placeholder="—" />
            </Campo>
            <Campo label="Glucosa"><input type="number" step="0.1" className={fieldCls} onChange={(e) => sigNum('glucosa', e.target.value)} /></Campo>
            <Campo label="Dolor (0-10)"><input type="number" min={0} max={10} className={fieldCls} onChange={(e) => sigNum('dolor', e.target.value)} /></Campo>
          </div>
          {(() => {
            const imc = calcIMC(signos.peso, signos.talla);
            const cl = clasificacionIMCEdad(imc, mesesEntre(fechaNacimiento, new Date().toISOString()), refIMC);
            return cl ? <p className="text-xs text-muted-foreground">IMC {imc} kg/m² · <span className={`rounded-full px-2 py-0.5 ${cl.cls}`}>{cl.label}</span></p> : null;
          })()}
        </section>
        </>)}

        <Diagnosticos dx={dx} setDx={setDx} />

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="outline" isLoading={guardar.isPending} onClick={() => guardar.mutate(false)}>Guardar borrador</Button>
          <Button isLoading={guardar.isPending} onClick={() => guardar.mutate(true)}><FileSignature className="w-4 h-4 mr-1" /> Guardar y firmar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Diagnósticos con búsqueda CIE-10 ----
function Diagnosticos({ dx, setDx }: { dx: Diagnostico[]; setDx: (d: Diagnostico[]) => void }) {
  const [q, setQ] = useState('');
  const { data: resultados = [] } = useQuery<Cie10[]>({
    queryKey: ['med-cie10', q],
    queryFn: () => medRpc<Cie10[]>('med_cie10_buscar', { _q: q }),
    enabled: q.length >= 2,
  });
  const add = (d: Diagnostico) => {
    if (d.cie10_codigo && dx.some((x) => x.cie10_codigo === d.cie10_codigo)) {
      toast.info(`El diagnóstico ${d.cie10_codigo} ya está agregado`); setQ(''); return;
    }
    setDx([...dx, d]); setQ('');
  };
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-muted-foreground">Diagnósticos</h4>
      {dx.length > 0 && (
        <ul className="text-sm divide-y border rounded-md">
          {dx.map((d, i) => (
            <li key={i} className="flex items-center justify-between px-3 py-1.5">
              <span>{d.cie10_codigo ? `${d.cie10_codigo} · ` : ''}{d.descripcion}</span>
              <span className="flex items-center gap-2">
                <select className="text-xs rounded border bg-background px-1 capitalize" value={d.tipo}
                  onChange={(e) => setDx(dx.map((x, j) => j === i ? { ...x, tipo: e.target.value } : x))}>
                  {TIPO_DIAGNOSTICO.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={d.es_principal} onChange={(e) => setDx(dx.map((x, j) => ({ ...x, es_principal: j === i ? e.target.checked : false })))} /> ppal</label>
                <button className="text-red-500" title="Quitar" aria-label="Quitar" onClick={() => setDx(dx.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <input className={fieldCls} placeholder="Buscar CIE-10 (código o descripción)…" value={q} onChange={(e) => setQ(e.target.value)} />
        {resultados.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-md border bg-card shadow-lg text-sm">
            {resultados.map((r) => (
              <li key={r.codigo}>
                <button className="w-full text-left px-3 py-1.5 hover:bg-muted" onClick={() => add({ cie10_codigo: r.codigo, descripcion: r.descripcion, tipo: 'definitivo', es_principal: dx.length === 0 })}>
                  <b>{r.codigo}</b> · {r.descripcion}
                </button>
              </li>
            ))}
            <li><button className="w-full text-left px-3 py-1.5 text-primary hover:bg-muted" onClick={() => add({ cie10_codigo: null, descripcion: q, tipo: 'presuntivo', es_principal: dx.length === 0 })}>+ Agregar «{q}» como diagnóstico local</button></li>
          </ul>
        )}
      </div>
    </section>
  );
}
